import {
  aRoleInstallation,
  peutConsulterInstallation,
  UtilisateurDroits,
} from './droits-installations';
import type { TicketColumnState } from './ticket-state';
import { validerDatesPlanification } from './intervention-dates';

/** Distingue la préparation d’une intervention du compte rendu d’une intervention réalisée. */
export type NatureIntervention = 'planification' | 'bon';
export type CategorieIntervention = 'curative' | 'preventive';

/** Formats partagés par le sélecteur de fichiers, la validation et la lecture des photos. */
export const TYPES_PHOTOS_INTERVENTION: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
];
export const TAILLE_MAX_PHOTO_INTERVENTION = 10 * 1024 * 1024;
export const NOMBRE_MAX_PHOTOS_INTERVENTION = 10;

/** Permissions d’affichage de la planification ; l’API reste responsable de l’autorisation. */
export type DroitsInterventions = {
  planifier: boolean;
  modifierPlanification: boolean;
};

export type UtilisateurIntervention = UtilisateurDroits & { id?: number | string };

export type InstallationInterventionOption = { id: number; nom: string };
export type TicketInterventionOption = {
  id: number;
  installationId: number;
  numero: string;
  libelle: string;
  etat?: TicketColumnState | null;
};
/** Préremplissage fourni par la page ; un ticket imposé verrouille le contexte en création. */
export type ContexteIntervention = {
  installationId?: number;
  ticketId?: number;
  /** Présélection du calendrier, sans conversion de fuseau et modifiable dans le formulaire. */
  date?: string;
  categorie?: CategorieIntervention;
};

/** Valeurs des champs avant validation, avec des identifiants textuels adaptés aux contrôles. */
export type BrouillonIntervention = {
  categorie: CategorieIntervention;
  installationId: string;
  ticketId: string;
  date: string;
  debut: string;
  fin: string;
  description: string;
  problemeResolu: boolean;
};

type SaisieBase = {
  id: number | null;
  installationId: number;
  description: string;
};
/** Saisie validée transmise au parent ; InterventionApi construit ensuite le contrat HTTP. */
export type SaisieIntervention =
  | (SaisieBase & {
      nature: 'planification';
      categorie: CategorieIntervention;
      ticketId: number | null;
      date: string;
      debut: string | null;
      fin: string | null;
    })
  | (SaisieBase & {
      nature: 'bon';
      ticketId: number;
      problemeResolu: boolean;
      nouvellesPhotos: readonly File[];
    });

/** Identité et valeurs initiales nécessaires à la modification et à la détection d’un brouillon. */
export type InterventionEditable = {
  id: number;
  nature: NatureIntervention;
  /** Identité du créateur fournie par le serveur, distincte du nom de l'intervenant. */
  auteurId: number | null;
  valeurs: BrouillonIntervention;
};

/** Choix disponibles servant aussi à vérifier l’appartenance du ticket à la centrale. */
export type OptionsIntervention = {
  installations: readonly InstallationInterventionOption[];
  tickets: readonly TicketInterventionOption[];
};

export type ErreurSaisieIntervention = {
  champ: keyof BrouillonIntervention;
  message: string;
};

/** Préremplit le contexte ; la présence d’un ticket oriente la planification vers le curatif. */
export function creerBrouillonIntervention(
  contexte: ContexteIntervention = {},
): BrouillonIntervention {
  return {
    categorie: contexte.ticketId ? 'curative' : (contexte.categorie ?? 'preventive'),
    installationId: contexte.installationId ? String(contexte.installationId) : '',
    ticketId: contexte.ticketId ? String(contexte.ticketId) : '',
    date: contexte.date ?? '',
    debut: '',
    fin: '',
    description: '',
    problemeResolu: false,
  };
}

/** Contrôle d'affichage uniquement : le serveur doit également contrôler l'autorisation. */
export function peutModifierBon(
  utilisateur: UtilisateurIntervention | null,
  auteurId: number | null,
  installationId?: number,
): boolean {
  if (!utilisateur) return false;
  if (utilisateur.roles.includes('ROLE_ADMIN')) return true;
  if (installationId !== undefined && aRoleInstallation(utilisateur, ['admin'], installationId))
    return true;
  const id = Number(utilisateur.id);
  return Number.isSafeInteger(id) && id > 0 && auteurId === id;
}

/** Supprimer exige l’accès à la centrale et la qualité d’auteur ou d’administrateur, pour toute nature. */
export function peutSupprimerIntervention(
  utilisateur: UtilisateurIntervention | null,
  intervention: InterventionEditable,
): boolean {
  const installationId = Number(intervention.valeurs.installationId);
  return (
    Number.isSafeInteger(intervention.id) &&
    intervention.id > 0 &&
    (intervention.nature === 'bon' ||
      (intervention.nature === 'planification' &&
        ['curative', 'preventive'].includes(intervention.valeurs.categorie))) &&
    peutConsulterInstallation(utilisateur, installationId) &&
    peutModifierBon(utilisateur, intervention.auteurId, installationId)
  );
}

/** Valide les choix métier et les horaires sans modifier le brouillon ni appeler l’API. */
export function validerSaisieIntervention(
  nature: NatureIntervention,
  valeurs: BrouillonIntervention,
  options: OptionsIntervention,
  maintenant = new Date(),
): ErreurSaisieIntervention[] {
  const erreurs: ErreurSaisieIntervention[] = [];
  const installationId = Number(valeurs.installationId);
  const ticketId = Number(valeurs.ticketId);
  if (!options.installations.some((i) => i.id === installationId)) {
    erreurs.push({ champ: 'installationId', message: 'Sélectionnez une centrale disponible.' });
  }
  if (
    (nature === 'bon' || valeurs.categorie === 'curative') &&
    !options.tickets.some((t) => t.id === ticketId && t.installationId === installationId)
  ) {
    erreurs.push({ champ: 'ticketId', message: 'Sélectionnez un ticket de cette centrale.' });
  }
  if (!valeurs.description.trim()) {
    erreurs.push({ champ: 'description', message: 'Renseignez la description de l’intervention.' });
  }
  if (nature === 'planification') {
    erreurs.push(...validerDatesPlanification(valeurs, maintenant));
  }
  return erreurs;
}

/** Valide puis convertit les champs en saisie typée ; lève une erreur au premier champ invalide. */
export function preparerSaisieIntervention(
  nature: NatureIntervention,
  valeurs: BrouillonIntervention,
  options: OptionsIntervention,
  id: number | null = null,
  photos: readonly File[] = [],
  maintenant = new Date(),
): SaisieIntervention {
  const erreurs = validerSaisieIntervention(nature, valeurs, options, maintenant);
  if (erreurs.length) throw new Error(erreurs[0].message);
  const base = {
    id,
    installationId: Number(valeurs.installationId),
    description: valeurs.description.trim(),
  };
  if (nature === 'planification') {
    return {
      ...base,
      nature,
      categorie: valeurs.categorie,
      // Une ancienne sélection de ticket ne doit pas être envoyée en préventif.
      ticketId: valeurs.categorie === 'curative' ? Number(valeurs.ticketId) : null,
      date: valeurs.date,
      // null permet aussi d’effacer un horaire existant lors d’une modification.
      debut: valeurs.debut || null,
      fin: valeurs.fin || null,
    };
  }
  return {
    ...base,
    nature,
    ticketId: Number(valeurs.ticketId),
    problemeResolu: valeurs.problemeResolu,
    nouvellesPhotos: [...photos],
  };
}
